// rounds/_shared types — shared across this module's features
import { z } from "zod";
// Imported as well as re-exported below: `wilayaName` takes a `Locale`, and a bare `export … from`
// does not bring the name into this file's scope.
import type { Locale } from "@core/locale";

/**
 * Every state `IntakeRound.Status` defines (verified against
 * `modules/acquire/models.py`) — not just the five this flow has commands for.
 *
 * The staff list endpoint returns `IntakeRound.objects.all()` with no status filter, so a
 * FROZEN, TRANSFERRED_TO_SELECT or ARCHIVED round WILL come down the wire. Enumerating only the
 * five "active" states made `parseSafe` drop those rows, and a round silently vanishing from an
 * administrator's list is worse than showing one whose buttons are quiet.
 *
 * Rendering them is required; inferring ACTIONS for them is not — the integration guide is
 * explicit that later lifecycle stories own those, so {@link RoundCard} offers none.
 */
export const RoundStatusSchema = z.enum([
  "DRAFT",
  "SCHEDULED",
  "OPEN",
  "CLOSING_SOON",
  "CLOSED",
  "FROZEN",
  "TRANSFERRED_TO_SELECT",
  "ARCHIVED",
]);

export type RoundStatus = z.infer<typeof RoundStatusSchema>;

/**
 * Pilot or live (`IntakeRound.OperatingMode`).
 *
 * US-E1-01's v2 amendment — "the pilot is a first-class appel, not a rehearsal, and its data must
 * stay distinguishable" — and it is ALREADY in the contract: readable on every round payload and
 * writable at create and on general PATCH. It is chosen at creation and freezes when the round
 * opens, so it travels with everything the round produces.
 */
export const OperatingModeSchema = z.enum(["PILOT", "LIVE"]);

export type OperatingMode = z.infer<typeof OperatingModeSchema>;

/**
 * The statuses the design calls « Appel en cours » — a round that is collecting dossiers.
 *
 * TWO of them, because `CLOSING_SOON` is a real status the lifecycle pass writes, not a display
 * flag. US-E1-08's v2 amendment says it SHOULD become a computed flag ("it is a timer; modelling it
 * as a state cost a transition, its tests and a badge"), and the design agrees — it draws
 * « Clôture dans 6 jours » as an inline alert on an OPEN card rather than as a status. Until the
 * backend follows, both statuses mean the same thing to this screen and neither gets its own filter.
 */
export const IN_PROGRESS_STATUSES = ["OPEN", "CLOSING_SOON"] as const;

/** Whether a round is currently collecting dossiers. */
export function isInProgress(status: RoundStatus): boolean {
  return status === "OPEN" || status === "CLOSING_SOON";
}

/**
 * Whether `GET /acquire/public/intake-rounds/{id}/` will actually resolve for this round —
 * mirrors `PUBLIC_DETAIL_STATUSES` in the backend's `public_services.py` exactly. A DRAFT round
 * (no `IntakeRoundConfigSnapshot` yet — that's only created at `open_intake_round`, and the public
 * read serves from it) 404s indistinguishably from a nonexistent id, by the backend's own explicit
 * design ("telling an anonymous reader that a round exists but is not published is a leak"). This
 * is also FROZEN/TRANSFERRED_TO_SELECT/ARCHIVED-safe: none of those carry a snapshot worth
 * previewing either, and the builder's own header uses this to keep « Aperçu » honestly disabled
 * rather than linking to a page that would 404.
 */
export function hasPublicPreview(status: RoundStatus): boolean {
  return (
    status === "SCHEDULED" ||
    status === "OPEN" ||
    status === "CLOSING_SOON" ||
    status === "CLOSED"
  );
}

/**
 * Whole FULL days until closing, measured against the SERVER clock.
 *
 * `server_now` travels with every round row for exactly this: a browser a few minutes fast would
 * otherwise round a round's last day down and announce a deadline that has not arrived. `null` for
 * anything not currently in progress — there is nothing counting down.
 *
 * `Math.floor`, not `Math.ceil`: with 2 hours left, `(closing - now) / 86_400_000` is ~0.08, and
 * `ceil` rounds that UP to 1 — reporting "closing in 1 day" for a round closing within the hour.
 * `floor` reads it as 0, which is what makes `<= 0` ("closing today") reachable at all for
 * anything short of a full day out. A round already past its deadline floors to a negative
 * number, which still satisfies `<= 0` the same way.
 *
 * Takes a structural shape rather than `RoundSummary` so `_shared` (which every feature builds ON)
 * never has to import a feature's own type.
 */
export function daysUntilClosing(round: {
  status: RoundStatus;
  closing_at: string | null;
  server_now: string;
}): number | null {
  if (!isInProgress(round.status) || round.closing_at === null) return null;
  const closing = new Date(round.closing_at).getTime();
  const now = new Date(round.server_now).getTime();
  if (Number.isNaN(closing) || Number.isNaN(now)) return null;
  return Math.floor((closing - now) / 86_400_000);
}

/**
 * Query shape for the staff round list. Lives in `_shared` rather than the `rounds` feature
 * because `keys.ts` — the one file another module may import — takes it as its list-key input.
 * `status: null` means "All statuses".
 */
export interface RoundListFilters {
  search: string;
  status: RoundStatus | null;
  page: number;
}

/**
 * One entry of the flat Wilaya catalogue (`GET /acquire/wilayas/`). Flat by design — the
 * integration guide forbids regions, Wilaya groups, or one-cohort-per-Wilaya UI, so there is
 * deliberately no parent/grouping field to render.
 */
export const WilayaSchema = z.object({
  /** Two-digit administrative code ("16") — the value sent in `wilaya_codes`. */
  code: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
});

export type Wilaya = z.infer<typeof WilayaSchema>;

/**
 * The wilaya's name in one locale. The STAFF catalogue returns all three
 * (`{code, name_fr, name_ar, name_en}`) — unlike the public one, which localizes server-side —
 * so the picker chooses per the interface language.
 */
export function wilayaName(wilaya: Wilaya, locale: Locale): string {
  return wilaya[`name_${locale}`];
}

/** Every localized name of a wilaya, for searching across languages. */
export function wilayaNames(wilaya: Wilaya): string[] {
  return [wilaya.name_fr, wilaya.name_ar, wilaya.name_en];
}

/**
 * Question input types accepted by the form builder. `CHECKBOX` is a SINGLE boolean, not a
 * multiple-choice control — `MULTI_SELECT` is the multi-answer type (integration guide §11).
 */
export const QuestionInputTypeSchema = z.enum([
  "SHORT_TEXT",
  "LONG_TEXT",
  "CHECKBOX",
  "RADIO",
  "DROPDOWN",
  "MULTI_SELECT",
  "URL",
  "NUMBER",
]);

export type QuestionInputType = z.infer<typeof QuestionInputTypeSchema>;

/**
 * The locale vocabulary now lives in `@core/locale` — `LOCALES`, `Locale`, `toLocale`,
 * `LocalizedText`, `emptyLocalizedText`, `localizedTextSchema`.
 *
 * It moved because none of it takes a domain type, so it was never module-specific: it is the
 * platform's trilingual contract, and the applicant surface needs exactly the same vocabulary. Left
 * here it would have had to be duplicated or reached across a module boundary.
 *
 * Re-exported so this module's consumers keep one import site for round vocabulary.
 */
export {
  LOCALES,
  emptyLocalizedText,
  localizedTextSchema,
  pickLocalized,
  toLocale,
  type Locale,
  type LocalizedText,
} from "@core/locale";

/** The input types that REQUIRE options; every other type forbids them. */
const OPTION_BEARING_INPUT_TYPES: readonly QuestionInputType[] = [
  "RADIO",
  "DROPDOWN",
  "MULTI_SELECT",
];

/** True when `type` must carry at least one option (RADIO / DROPDOWN / MULTI_SELECT). */
export function requiresOptions(type: QuestionInputType): boolean {
  return OPTION_BEARING_INPUT_TYPES.includes(type);
}
